import { useContext, useMemo, useState } from 'react'

import {
  assigneesStateFamily,
  useCoordinator,
  useCurrentUser,
  UUID,
  workDocSectionAtom,
  ApprovalState,
} from '@builddocs/data-management'
import { FaceControlContext } from '@builddocs/feature-faceid'
import { ActFormKeys, useAct } from '@builddocs/filestore-api'
import { handleDeviate, InspectorsRemovedNotice } from '@builddocs/mui'
import { AosrDateChangeNotice, AosrBadCertificateNotice } from '@builddocs/mui'
import { useToast } from '@builddocs/system'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useRecoilValue, useRecoilValueLoadable } from 'recoil'

import {
  getAssignees,
  handleToInspect,
  handleToSigning,
  initActSigning,
} from '../Utils'

import { ChooseSignAction } from './ChooseSignAction'
import InspectorAction from './InspectorAction'
import { SingingPoaInfo } from './SingingPoaInfo'

type OwnProps = {
  actId: UUID
  actType: ActFormKeys
  toReopenInspection: boolean
  canShowSigningActions: boolean
  rights: {
    canEdit: boolean
    canInspect: boolean
    canSendActToSigning: boolean
    canSignAct: boolean
  }
  onUpdatePDF?: () => void
}

export const SigningActTasks = ({
  actId,
  actType,
  toReopenInspection,
  canShowSigningActions,
  rights,
  onUpdatePDF,
}: OwnProps) => {
  const { toast } = useToast()
  const { user } = useCurrentUser()
  const { contractId, projectId } = useCoordinator() as {
    contractId: string
    projectId: string
  }
  const selectedWorkDocSection = useRecoilValue(workDocSectionAtom(contractId))
  const faceControl = useContext(FaceControlContext)
  const { act, isLoading, refreshAct } = useAct({ actId, actType })
  const assigneesTemp = useRecoilValueLoadable(assigneesStateFamily(contractId))
  const [, setIsUpdating] = useState(false)
  const [, setIsNeedUpdate] = useState(false)

  // Фильтруем подписантов с withPoa: true и собираем их poaId
  const signersWithPoa = useMemo(() => {
    if (!act?.assignees) return []
    return act.assignees
      .filter((assignee: any) => assignee.withPoa === true && assignee.poaId)
      .map((assignee: any) => ({
        fio: assignee.name || '',
        poaId: assignee.poaId,
        signingDateTime: assignee.signingDateTime,
      }))
  }, [act])

  console.log(act)

  return (
    <Box>
      <Typography gutterBottom variant="h6">
        Согласование и подписание
      </Typography>
      {isLoading && <Skeleton />}
      {act && (
        <>
          {act.actType === 'AOSR' && (
            <AosrDateChangeNotice
              act={act}
              projectId={projectId}
              sx={{ marginBottom: '8px' }}
            />
          )}
          {act.actType === 'AOSR' &&
            act.approvalStatus !== ApprovalState.SIGNED && (
              <AosrBadCertificateNotice act={act} />
            )}
          <InspectorsRemovedNotice actBannerTypes={act.actBannerTypes} />
          <Stack direction="row" spacing={2}>
            <InspectorAction
              act={act}
              canInspect={rights.canInspect}
              canSendToSigning={rights.canSendActToSigning}
              handleDeviate={(...args: any[]) =>
                handleDeviate({
                  act,
                  contractId,
                  body: args[0],
                  user,
                  additionalInfo: args[1],
                  onSuccess: args[2],
                  refreshActs: refreshAct,
                  action: 'inspection',
                  // @ts-expect-error затычка
                  actType: act.actType,
                })
              }
              handleToInspection={() =>
                handleToInspect({
                  user,
                  selectedInspectors: getAssignees(assigneesTemp, act.workType)
                    .inspectors,
                  setIsLoading: setIsUpdating,
                  act,
                  projectId,
                  contractId,
                  selectedWorkDocSection,
                  refreshAct,
                  toast,
                })
              }
              isDisabled={isLoading}
              userEmploymentId={user.employment?.employmentId}
            />
            <ChooseSignAction
              act={act}
              canSendToSigning={rights.canSendActToSigning}
              canShowSigningActions={canShowSigningActions}
              canSign={rights.canSignAct}
              handleDeviate={(...args) =>
                handleDeviate({
                  act,
                  contractId,
                  body: args[0],
                  user,
                  additionalInfo: args[1],
                  onSuccess: args[2],
                  refreshActs: refreshAct,
                  action: 'signing',
                  // @ts-expect-error затычка
                  actType: act.actType,
                  faceControl,
                })
              }
              handleToSigning={() =>
                handleToSigning({
                  isEmbedded: true,
                  user,
                  selectedSigners: getAssignees(assigneesTemp, act.workType)
                    .signers,
                  setIsLoading: setIsUpdating,
                  act,
                  projectId,
                  contractId,
                  selectedWorkDocSection,
                  refreshAct: () => {
                    refreshAct()
                    onUpdatePDF && onUpdatePDF()
                  },
                  setIsNeedUpdate,
                  faceControl,
                })
              }
              initActSigning={() =>
                initActSigning({
                  isEmbedded: true,
                  user,
                  selectedSigners: getAssignees(assigneesTemp, act.workType)
                    .signers,
                  setIsLoading: setIsUpdating,
                  act,
                  projectId,
                  contractId,
                  selectedWorkDocSection,
                  refreshAct: () => {
                    refreshAct()
                    onUpdatePDF && onUpdatePDF()
                  },
                  setIsNeedUpdate,
                  faceControl,
                })
              }
              isDisabled={isLoading}
              refreshAfterSign={() => {
                refreshAct()
                onUpdatePDF && onUpdatePDF()
              }}
              toReopenInspection={toReopenInspection}
              userEmploymentId={user.employment?.employmentId}
            />
          </Stack>
          <SingingPoaInfo
            signersWithPoa={signersWithPoa}
            title="Информация о доверенностях"
            user={user}
          />
        </>
      )}
    </Box>
  )
}

export default SigningActTasks
